/**
 * MyInformation — the logged-in user's saved profile form (account page).
 *
 * phone (optional), "I Am A" (required, admin-managed), and a DEFAULT location
 * (country required; region required when the country has subdivisions; city +
 * postal optional). Saves to /api/auth-profile.php and refreshes AuthContext.
 * These values pre-fill the estimate save modal and are synced to Keycloak
 * (best-effort) by the backend.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle2, UserCog } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { saveUserProfile, fetchGeoCountries, UserProfile } from '../../services/api';
import { fetchUserCategories } from '../../services/userCategoriesApi';
import { UserCategory, USER_CATEGORY_FALLBACK } from '../../data/userCategories';
import { GeoCountry, fallbackGeo, regionsFor, regionLabelFor } from '../../data/regions';

const EMPTY: UserProfile = { phone: '', userCategory: '', country: '', region: '', city: '', postalCode: '' };

export const MyInformation: React.FC = () => {
  const { t, i18n } = useTranslation('account');
  const { profile, refreshProfile } = useAuth();
  const [form, setForm] = useState<UserProfile>(EMPTY);
  const [categories, setCategories] = useState<UserCategory[]>(USER_CATEGORY_FALLBACK);
  const [geo, setGeo] = useState<GeoCountry[]>(fallbackGeo);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate the form from the server-owned profile.
  useEffect(() => { if (profile) setForm({ ...EMPTY, ...profile }); }, [profile]);

  // Admin-managed option sources (fall back silently).
  useEffect(() => {
    let cancelled = false;
    // Category and country names are admin-managed text translated
    // server-side, so a language switch has to re-fetch them.
    fetchUserCategories().then(c => { if (!cancelled && c) setCategories(c); });
    fetchGeoCountries().then(g => { if (!cancelled && g) setGeo(g); });
    return () => { cancelled = true; };
  }, [i18n.language]);

  const regions = useMemo(() => regionsFor(geo, form.country), [geo, form.country]);
  const regionLabel = regionLabelFor(geo, form.country);
  const regionRequired = regions.length > 0;

  const set = (field: keyof UserProfile, value: string) => {
    setSaved(false);
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'country') next.region = ''; // reset region when country changes
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    if (!form.userCategory.trim()) { setError(t('info.errors.category')); return; }
    if (!form.country.trim()) { setError(t('info.errors.country')); return; }
    // regionLabel is admin-managed DB text ("State"/"Province"/"Prefecture"),
    // interpolated rather than translated here — it belongs to the Phase 3
    // content overlay.
    if (regionRequired && !form.region.trim()) { setError(t('info.errors.region', { region: regionLabel.toLowerCase() })); return; }
    setSaving(true);
    try {
      await saveUserProfile(form);
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError(t('info.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card shadow-sm mb-4 px-2 px-sm-0">
      <div className="card-body px-0 px-sm-4 py-4">
        <h5 className="fw-bold m-0 mb-3 d-flex align-items-center gap-2"><UserCog size={18} /> {t('tabs.information')}</h5>

        {error && <div className="alert alert-warning py-2 small">{error}</div>}
        {saved && <div className="alert alert-success py-2 small d-flex align-items-center gap-2"><CheckCircle2 size={16} /> {t('info.saved')}</div>}

        <div className="row g-3">
          <div className="col-12 col-sm-6">
            <label className="form-label small fw-semibold">{t('info.phone')}</label>
            <input type="tel" className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder={t('info.optional')} />
          </div>
          <div className="col-12 col-sm-6">
            <label className="form-label small fw-semibold">{t('info.iAmA')} <span className="text-danger">*</span></label>
            <select className="form-select" value={form.userCategory} onChange={e => set('userCategory', e.target.value)}>
              <option value="" disabled>{t('info.select')}</option>
              {/* Category and country names are admin-managed DB text — Phase 3. */}
              {/* value is the ENGLISH name — it is submitted and stored, so it must not
                  vary by the visitor's language. The label is the translated one. */}
              {categories.map(c => <option key={c.slug} value={c.nameEn}>{c.name}</option>)}
            </select>
          </div>

          <div className="col-12 col-sm-6">
            <label className="form-label small fw-semibold">{t('info.country')} <span className="text-danger">*</span></label>
            <select className="form-select" value={form.country} onChange={e => set('country', e.target.value)}>
              <option value="" disabled>{t('info.select')}</option>
              {geo.map(c => <option key={c.isoCode || c.nameEn} value={c.nameEn}>{c.name}</option>)}
            </select>
          </div>
          <div className="col-12 col-sm-6">
            <label className="form-label small fw-semibold">
              {regionLabel}{regionRequired && <span className="text-danger"> *</span>}
            </label>
            <select className="form-select" value={form.region} disabled={!form.country || !regionRequired} onChange={e => set('region', e.target.value)}>
              <option value="">{form.country
                ? (regionRequired ? t('info.selectRegion', { region: regionLabel.toLowerCase() }) : t('info.notApplicable'))
                : t('info.selectCountryFirst')}</option>
              {regions.map(r => <option key={r.code} value={r.valueEn}>{r.value}</option>)}
            </select>
          </div>

          <div className="col-12 col-sm-6">
            <label className="form-label small fw-semibold">{t('info.city')}</label>
            <input className="form-control" value={form.city} onChange={e => set('city', e.target.value)} placeholder={t('info.optional')} />
          </div>
          <div className="col-12 col-sm-6">
            <label className="form-label small fw-semibold">{t('info.postalCode')}</label>
            <input className="form-control" value={form.postalCode} onChange={e => set('postalCode', e.target.value)} placeholder={t('info.optional')} />
          </div>
        </div>

        <div className="mt-3">
          <button className="btn btn-primary d-inline-flex align-items-center gap-2" disabled={saving} onClick={handleSave}>
            {saving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />} {t('info.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyInformation;
